import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Copy, Check } from "lucide-react";

function CodeBlock({ inline, className, children, ...props }) {
  const [copied, setCopied] = React.useState(false);
  const text = String(children).replace(/\n$/, "");
  const lang = /language-(\w+)/.exec(className || "")?.[1] || "code";

  if (inline) {
    return (
      <code className="px-1.5 py-0.5 rounded-md bg-slate-100 text-indigo-800 text-[0.85em] font-mono" {...props}>
        {children}
      </code>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  return (
    <div className="my-4 rounded-xl overflow-hidden border border-slate-800/40 bg-[#0F172A]" data-testid="code-block">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#0B1120] border-b border-slate-800/60">
        <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">{lang}</span>
        <button
          onClick={copy}
          className="text-[11px] font-medium text-slate-300 hover:text-white inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-slate-700/40"
          data-testid="code-copy-button"
        >
          {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
        </button>
      </div>
      <pre className="px-4 py-3 overflow-x-auto text-[13px] leading-relaxed font-mono text-slate-100">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

export default function Markdown({ content }) {
  return (
    <div className="prose-stella max-w-none text-slate-800 leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          code: CodeBlock,
          a: ({ node, ...props }) => (
            <a {...props} className="text-indigo-700 underline underline-offset-2 hover:text-indigo-900" target="_blank" rel="noreferrer" />
          ),
          table: ({ node, ...props }) => (
            <div className="my-3 overflow-x-auto"><table className="w-full text-sm border-collapse" {...props} /></div>
          ),
          th: ({ node, ...props }) => <th className="text-left font-semibold border-b border-slate-200 px-3 py-2 bg-slate-50" {...props} />,
          td: ({ node, ...props }) => <td className="border-b border-slate-100 px-3 py-2 align-top" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-1 my-2" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-1 my-2" {...props} />,
          h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-4 mb-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-base font-semibold mt-3 mb-1" {...props} />,
          p: ({ node, ...props }) => <p className="my-2" {...props} />,
        }}
      >
        {content || ""}
      </ReactMarkdown>
    </div>
  );
}
